/**
 * Reconcile the client's in-flight swaps against the server's ORIGIN axis.
 *
 * THE SERVER IS THE RECORD, THE CLIENT IS A CACHE. A swap survives a closed tab,
 * a crashed renderer and a new device; localStorage survives none of them. On
 * every resume the client asks what the server actually knows and adopts it,
 * rather than continuing to render whatever it happened to write down last.
 *
 * This does NOT touch the provider axis. Settlement is still the swap tracker's
 * job and its answer comes from Relay or Across, not from a chain.
 */

import { getSwapOrigins, type SwapOriginRecord } from './swapApi';
import { setPendingSwapOriginStatus } from '@/store/store';
import { getSlice } from '@/store/store';
import type { PendingSwap } from './types';

/** One adoption: a tracked swap and the origin status the server reports. */
export interface OriginPatch {
  id: string;
  originStatus: string;
}

/**
 * Work out which local records the server has a newer origin answer for.
 *
 * PURE, so the matching rule can be tested without a network or a store. The
 * rule itself is deliberately narrow:
 *
 *   - Matched by TRACKING ID ONLY. Never by provider + request id, and never by
 *     transaction hash: the tracking id is the one identifier the server issued
 *     and the client stored, so a match cannot be a coincidence.
 *   - A local record with no tracking id is SKIPPED, not guessed at. It predates
 *     this feature, and inventing an origin status for it would put a state on
 *     screen that nothing ever observed.
 *   - A server row the client has never seen is NOT adopted here. It has no
 *     amounts, symbols or chain names, so a record built from it would render a
 *     swap with blank everything. Surfacing those is a separate piece of work.
 *
 * @param local The client's in-flight swaps, keyed by transaction id.
 * @param remote What the server reports for this org.
 * @returns One patch per record whose origin status actually changed.
 */
export function planOriginAdoption(
  local: Record<string, PendingSwap>,
  remote: SwapOriginRecord[],
): OriginPatch[] {
  const byTracking = new Map<string, SwapOriginRecord>();
  for (const row of remote) {
    if (row.tracking_id) byTracking.set(row.tracking_id, row);
  }

  const patches: OriginPatch[] = [];
  for (const swap of Object.values(local)) {
    // Defence in depth, not the load-bearing check: the map above already
    // refuses to key a row by an empty id, so a legacy record with no tracking
    // id cannot match anything even without this line. It stays so the rule is
    // visible where the reader is looking, and so a future change to the map
    // cannot silently re-open the path. (Mutation testing reports it as an
    // equivalent mutant for that reason -- removing it changes no behaviour.)
    if (!swap.trackingId) continue;
    const row = byTracking.get(swap.trackingId);
    if (!row || !row.origin_status) continue;
    // Unchanged means nothing to write: a resume that finds nothing new must not
    // churn storage or re-render every banner.
    if (row.origin_status === swap.originStatus) continue;
    patches.push({ id: swap.id, originStatus: row.origin_status });
  }
  return patches;
}

/**
 * Ask the server for this org's origin answers and adopt them.
 *
 * BEST EFFORT. A resume that cannot reach the server leaves the client showing
 * what it already had, which is the honest fallback -- the alternative would be
 * blanking states the user is watching because one request failed.
 *
 * @param opts Options.
 * @returns The patches applied, for logging or tests.
 */
export async function reconcileOrigins(opts: { signal?: AbortSignal } = {}): Promise<OriginPatch[]> {
  let remote: SwapOriginRecord[];
  try {
    remote = await getSwapOrigins({ signal: opts.signal });
  } catch {
    // Swallowed deliberately: see above.
    return [];
  }
  const patches = planOriginAdoption(getSlice('pendingSwaps').byId, remote);
  for (const patch of patches) {
    setPendingSwapOriginStatus(patch.id, patch.originStatus);
  }
  return patches;
}
